import type { IRealtimeService } from './IRealtimeService';
import type { WebsocketMessage } from '@playcastdotio/Messaging';
import { isSensitiveMessage } from '@playcastdotio/Messaging';
import * as realtimeConnection from './RealtimeConnection';
import { attachSentryTraceToMessage } from '../../api/modules/realtime/tracing';
import type { RealtimeConnectionAnalytics } from './RealtimeConnection';

export interface RealtimeConnectionPort {
  sendMessage(message: WebsocketMessage): void;
  trySendMessage(message: WebsocketMessage): boolean;
  isConnectionReady(): boolean;
  onConnected(listener: () => void): () => void;
  onDisconnected(listener: () => void): () => void;
  onHostAuthSuccess(listener: () => void): () => void;
  setNetworkAnalytics(analytics: RealtimeConnectionAnalytics): void;
}

/** Production adapter; socket ownership and queue policy stay in the connection service. */
export class RealRealtimeService implements IRealtimeService {
  private analyticsConfigured = false;
  constructor(
    private readonly connection: RealtimeConnectionPort = realtimeConnection,
    private readonly analytics?: RealtimeConnectionAnalytics,
  ) {}
  private activeConnection(): RealtimeConnectionPort {
    if (!this.analyticsConfigured && this.analytics) {
      this.connection.setNetworkAnalytics(this.analytics);
      this.analyticsConfigured = true;
    }
    return this.connection;
  }
  sendMessage(message: WebsocketMessage): void {
    const traced = isSensitiveMessage(message) ? message : attachSentryTraceToMessage(message);
    this.activeConnection().sendMessage(traced);
  }
  trySendMessage(message: WebsocketMessage): boolean { return this.activeConnection().trySendMessage(message); }
  isConnected(): boolean { return this.activeConnection().isConnectionReady(); }
  onConnected(listener: () => void): () => void { return this.activeConnection().onConnected(listener); }
  onDisconnected(listener: () => void): () => void { return this.activeConnection().onDisconnected(listener); }
  onHostAuthSuccess(listener: () => void): () => void { return this.activeConnection().onHostAuthSuccess(listener); }
}
